import Breadcrumbs from '@mui/material/Breadcrumbs'
import Link from '@mui/material/Link'
import Box from '@mui/material/Box'
import Container from '@mui/material/Container'
import Grid from '@mui/material/Grid'
import Paper from '@mui/material/Paper'
import Stack from '@mui/material/Stack'
import TextField from '@mui/material/TextField'
import Typography from '@mui/material/Typography'
import { KNHomeIcon } from 'components/KNIcon/KNIcon'
import { getTheHulkCategories, getTheHulkPages } from 'modules/TheHulk/TheHulk.data'
import { cleanHulkQuestions, filterHulkCategories } from 'modules/TheHulk/TheHulk.helpers'
import type { FaqCategory, TheHulkMessageProps } from 'modules/TheHulk/TheHulk.types'
import { type ReactElement, useEffect, useState } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import FaqCategoryCard from 'screens/Faq/FaqCategoryCard'
import FaqQuestionCard from 'screens/Faq/FaqQuestionCard'

const FaqPage = (): ReactElement => {
  const [theHulkCategories, setTheHulkCategories] = useState<FaqCategory[] | null>(null)
  const [theHulkQuestions, setTheHulkQuestions] = useState([])
  const faqQuestions: TheHulkMessageProps[] | null = cleanHulkQuestions(theHulkQuestions)
  const [selectedCategory, setSelectedCategory] = useState<FaqCategory>()
  const [breadCrumbs, setBreadCrumbs] = useState<string[]>([''])
  const [searchTerm, setSearchTerm] = useState('')
  const [searchResults, setSearchResults] = useState<TheHulkMessageProps[]>([])
  const [allQuestionsAndAnswers, setAllQuestionsAndAnswers] = useState<TheHulkMessageProps[]>([])
  const { t } = useTranslation()

  useEffect(() => {
    const fetchTheHulkCategories = async (): Promise<void> => {
      try {
        const response = await getTheHulkCategories()
        setTheHulkCategories(filterHulkCategories(response?.data))
      } catch (error) {
        console.log('error', error)
      }
    }
    void fetchTheHulkCategories()
    setDefaultBreadCrumbs()
  }, [])

  const getAllQuestionsAndAnswers = async (): Promise<TheHulkMessageProps[]> => {
    if (!theHulkCategories) {
      return []
    }
    const allQuestionsAndAnswers = (
      await Promise.all(
        theHulkCategories.map(async ({ key }) => {
          const faqQuestions = await getTheHulkPages(key)
          return cleanHulkQuestions(faqQuestions.data.pages)
        }),
      )).filter((x): x is TheHulkMessageProps[] => Array.isArray(x))
      .flat()
    setAllQuestionsAndAnswers(allQuestionsAndAnswers)
    return allQuestionsAndAnswers
  }

  const setDefaultBreadCrumbs = (): void => {
    setBreadCrumbs([t('screens.cs.faq.faqHome')])
  }

  const handleCategoryClick = (category: FaqCategory): void => {
    setSelectedCategory(category)
    setBreadCrumbs([...breadCrumbs, category.name])
  }

  useEffect(() => {
    const fetchTheHulkQuestions = async (): Promise<void> => {
      if (selectedCategory?.key) {
        try {
          const response = await getTheHulkPages(selectedCategory.key)
          setTheHulkQuestions(response?.data.pages)
        } catch (error) {
          console.log('error', error)
        }
      }
    }
    void fetchTheHulkQuestions()
  }, [selectedCategory])

  const getFAQHomeElement = (label: string, color: string): ReactElement => {
    return (<Stack direction="row" key={label} sx={{ color: color }} display="inline-block">
      <KNHomeIcon color="inherit" fontSize="small"></KNHomeIcon>
      <Typography
        key={label}
        aria-current="page"
        variant="p1"
        sx={{ paddingLeft: 1 }}
      >
        {label}
      </Typography>
    </Stack>)
  }

  const performSearch = async (): Promise<void> => {
    const questionsAndAnswers = allQuestionsAndAnswers.length > 0 ? allQuestionsAndAnswers : await getAllQuestionsAndAnswers()
    if (questionsAndAnswers.length > 0) {
      const searchResults = questionsAndAnswers.filter((question) =>
        question?.content.toLowerCase().includes(searchTerm.toLowerCase()) || question?.title.toLowerCase().includes(searchTerm.toLowerCase()),
      )
      setSearchResults(searchResults)
      if (!breadCrumbs.includes(t('screens.cs.faq.searchResults'))) {
        setBreadCrumbs([...breadCrumbs, t('screens.cs.faq.searchResults')])
      }
    } else {
      setSearchResults([])
      setDefaultBreadCrumbs()
    }
  }

  useEffect(() => {
    if (searchTerm.length >= 3) {
      // eslint-disable-next-line @typescript-eslint/no-floating-promises
      performSearch()
    } else {
      setSearchResults([])
      setDefaultBreadCrumbs()
    }
  }, [searchTerm])

  const handleBreadCrumbClick = (event: React.MouseEvent<HTMLAnchorElement, MouseEvent>): void => {
    event.preventDefault()
    setSelectedCategory(undefined)
    setDefaultBreadCrumbs()
    setTheHulkQuestions([])
    setSearchResults([])
    setSearchTerm('')
  }

  return (
    <Container maxWidth="xxl">
      <Grid container data-test="release-notes-page-container">
        <Grid size={{ xs: 12, md: 8, xl: 6 }}>
          <Paper
            sx={{
              p: 2,
              width: '60vw',
            }}
          >
            <Breadcrumbs aria-label="breadcrumb" sx={{ padding: 2 }}>
              {breadCrumbs.map((label, index) => {
                const isLast = index === breadCrumbs.length - 1

                if (isLast) {
                  if (index === 0) {
                    return getFAQHomeElement(label, 'info.light')
                  } else {
                    return (
                      <Typography
                        key={label}
                        aria-current="page"
                        variant="p3"
                        color="info.light"
                      >
                        {label}
                      </Typography>
                    )
                  }
                }

                return (
                  <Link key={label}
                    onClick={(event) => {
                      handleBreadCrumbClick(event)
                    }}
                    underline="hover" color="inherit" sx={{ cursor: 'pointer' }}>
                    {getFAQHomeElement(label, 'primary.main')}
                  </Link>)

              })}
            </Breadcrumbs>

            <Box p={2}>
              <Typography variant="h3" color="primary.main">
                {searchTerm.length >= 3 ? t('screens.cs.faq.searchResults') :
                  t('screens.cs.faq.screenName')}

              </Typography>
            </Box>
            {searchTerm.length < 3 && <Box p={2}>
              <Typography variant="p3" color="primary.main">
                <Trans i18nKey="screens.cs.faq.introduction" />
              </Typography>
            </Box>}
            <Box p={2}>
              <TextField
                label={t('screens.cs.trip_dashboard.filters.search')}
                value={searchTerm}
                fullWidth
                onChange={(e) => setSearchTerm(e.target.value)}
              ></TextField>
            </Box>
            {selectedCategory && searchTerm.length < 3 &&
              <FaqQuestionCard
                faqQuestions={faqQuestions}
                selectedCategory={selectedCategory}
              />}
            {!selectedCategory && searchTerm.length < 3 &&
              <FaqCategoryCard
                faqCategories={theHulkCategories ?? null}
                onAction={handleCategoryClick} />
            }
            {searchTerm.length >= 3 &&
              <FaqQuestionCard
                faqQuestions={searchResults}
                selectedCategory={{
                  key: 'search',
                  name: t('screens.cs.faq.searchAmountString', {
                    searchResultsLength: searchResults.length.toString(),
                    searchTerm: searchTerm,
                  }),
                  description: '',
                }}
                searchTerm={searchTerm} />
            }
          </Paper>
        </Grid>
      </Grid>
    </Container>
  )
}

export default FaqPage